import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  Unstable_Grid2 as Grid,
  IconButton,
  MenuItem,
  Stack,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material';
import PlusIcon from '@untitled-ui/icons-react/build/esm/Plus';
import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useParams } from 'react-router';
import infiniteCanvasApi from 'src/api/infiniteCanvas/InfiniteCanvasApi';
import ConfirmPopover from 'src/components/confirm-popover';
import { Seo } from 'src/components/seo';
import { isExcludedFeature } from 'src/config';
import { usePermissionGuard } from 'src/guards/organization-guard';
import { useDialog } from 'src/hooks/use-dialog';
import { useRouter } from 'src/hooks/use-router';
import { TEST_IDS } from 'src/test-ids';
import $log from 'src/log/log';
import { paths } from 'src/paths';
import { ListCanvas } from 'src/sections/components/grid-lists/grid-list-canvas/infinite-canvas-list';
import { thunks } from 'src/thunks/project';
import { Canvas } from 'src/types/canvas';
import { generateCanvasId } from 'src/utils/generateCanvasId';
import { EditableTextField } from '../../components/textfield/EditableTextField';
import { usePageView } from '../../hooks/use-page-view';
import { useSettings } from '../../hooks/use-settings';
import { useDispatch, useSelector } from '../../store/index';
const useProject = () => {
  const params = useParams();
  const dispatch = useDispatch();

  const handleChange = useCallback(() => {
    if (params.projectId) {
      dispatch(thunks.getProject({ id: params.projectId }));
      dispatch(thunks.setCurrentProject({ currentId: params.projectId }));
    }
  }, [dispatch, params.projectId]);

  useEffect(() => {
    handleChange();
    return () => {};
  }, [handleChange]);
};

export const InfiniteCanvasTemplateDropDown = ({
  value,
  onChange,
  disabled,
}: {
  value?: string;
  onChange?: (event: any) => void;
  disabled?: boolean;
}) => {
  const [templates, setTemplates] = useState([]);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchTemplates = useCallback(() => {
    if (hasFetched) {
      return;
    }
    $log.debug('fetching templates', hasFetched);
    infiniteCanvasApi.listTemplates().then((res) => {
      if (res.ok) {
        setTemplates(res.result);
        setHasFetched(true);
      } else {
        toast.error('Failed to fetch templates');
      }
    });
  }, [hasFetched]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);
  fetchTemplates();
  return (
    <TextField
      select
      disabled={disabled}
      label="Select Template"
      value={value || ''}
      onChange={onChange}
      variant="outlined" // You can use "standard" or "filled" variant as well
      fullWidth // Optional: to make the dropdown full width
      margin="normal" // Optional: adds margin to the input
      data-testid={TEST_IDS.CANVAS_FIELD.TEMPLATE_SELECT}
    >
      {/* Define the options here */}
      <MenuItem value="empty">
        <em>Empty canvas</em>
      </MenuItem>
      {templates.map((template: any) => (
        <MenuItem key={template.id} value={template.id}>
          {template.name}
        </MenuItem>
      ))}
      {/* <MenuItem value="option1">Option 1</MenuItem>
            <MenuItem value="option2">Option 2</MenuItem>
            <MenuItem value="option3">Option 3</MenuItem> */}
    </TextField>
  );
};

export default function Overview() {
  const settings = useSettings();
  // useProject();
  usePageView();
  const { teamId } = useParams();
  const dispatch = useDispatch();
  const router = useRouter();
  const project = useSelector((state) => state.project.project);
  const [canvases, setCanvases] = useState(project?.canvases?.items || []);
  const permissions = usePermissionGuard({
    permissions: ['admin', 'editor', 'organization', 'viewer'],
  });
  const [selectedTemplate, setSelectedTemplate] = useState('empty');
  const [newCanvasName, setNewCanvasName] = useState('');

  const handleNameChange = (event: any) => {
    setNewCanvasName(event.target.value);
  };

  const dialog = useDialog();

  const handleTemplateChange = (event: any) => {
    setSelectedTemplate(event.target.value);
  };

  const handleEmpty = useCallback(() => {
    if (!project) return;

    if (!teamId) return;
    $log.debug('Creating empty canvas');
    router.push(
      paths.infiniteCanvas.canvas(teamId, generateCanvasId(), {
        name: newCanvasName,
      }),
    );
  }, [project, router, teamId, newCanvasName]);

  useEffect(() => {
    setCanvases(project?.canvases?.items || []);
  }, [project?.canvases?.items]);

  const handleCreateInfiniteCanvas = () => {
    $log.debug('Creating infinite canvas with template', selectedTemplate);
    if (!newCanvasName) {
      toast.error('Please enter a name for the canvas');
      return;
    }

    if (selectedTemplate === 'empty') {
      handleEmpty();
    } else {
      if (!project) return;
      const projectId = project.id;
      if (!teamId) return;
      $log.debug('Creating infinite canvas with template', selectedTemplate);
      router.push(
        paths.infiniteCanvas.canvas(teamId, generateCanvasId()) +
          '?template=' +
          selectedTemplate +
          '&name=' +
          newCanvasName,
      );
    }
  };

  return (
    <>
      <Seo title="Projects: Overview" />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 8,
        }}
      >
        <Container maxWidth={settings.stretch ? false : 'xl'}>
          <Grid
            container
            disableEqualOverflow
            spacing={{
              xs: 3,
              lg: 4,
            }}
          >
            <Grid xs={12}>
              <Stack direction="row" justifyContent="space-between" spacing={4}>
                <Stack direction={'row'}>
                  <EditableTextField
                    typograhyProps={{ variant: 'h4' }}
                    value={project?.name}
                    onChange={(name) => {
                      dispatch(thunks.updateProject({ id: project!.id, name }));
                    }}
                  ></EditableTextField>
                  <ConfirmPopover
                    content={'Are you sure you want to delete this project?'}
                    onCancel={() => {}}
                    onConfirm={() => {
                      dispatch(thunks.deleteProject({ id: project!.id }));
                    }}
                    title={''}
                    actionProps={{ confirmButtonProps: { color: 'error' } }}
                    actionLabels={{
                      confirmButtonLabel: 'Delete',
                      cancelButtonLabel: 'Cancel',
                    }}
                    component={(handleClick) => (
                      <IconButton color="error" onClick={handleClick}>
                        <DeleteIcon></DeleteIcon>
                      </IconButton>
                    )}
                  ></ConfirmPopover>
                </Stack>
              </Stack>
            </Grid>
            <Grid xs={12}>
              {permissions.canEdit && (
                <Stack direction="row" spacing={4}>
                  <Button
                    startIcon={
                      <SvgIcon>
                        <AllInclusiveIcon></AllInclusiveIcon>
                      </SvgIcon>
                    }
                    variant="contained"
                    onClick={dialog.handleOpen}
                    title={'Create a new infinite canvas'}
                  >
                    New Infinite Canvas
                  </Button>
                </Stack>
              )}
              <ListCanvas canvases={canvases.filter(Boolean) as Canvas[]} />
            </Grid>
          </Grid>
          <Grid
            container
            disableEqualOverflow
            spacing={{
              xs: 3,
              lg: 4,
            }}
          ></Grid>
        </Container>
      </Box>
      <Dialog open={dialog.open} onClose={dialog.handleClose} fullWidth>
        <DialogContent>
          <Typography variant="h6">New Infinite canvas</Typography>
          <TextField label="Name" variant="outlined" fullWidth onChange={handleNameChange} margin="normal"></TextField>
          <InfiniteCanvasTemplateDropDown onChange={handleTemplateChange} />
        </DialogContent>
        <DialogActions>
          <Button onClick={dialog.handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateInfiniteCanvas}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
